/**
 * Msg 566 · F.6 — Merge duplicate topics modal.
 *
 * Detects near-duplicate child names on any focus view and offers a
 * one-tap merge into a canonical topic. Detection uses a normalized
 * token-set Jaccard similarity (mirrors backend's dedupe heuristic).
 *
 * The merge itself is a single atomic backend call:
 *   POST /api/atlas/topics/merge
 * which reparents children, rewrites facts, moves attachments, and
 * deletes duplicates in one server round-trip.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  X as CloseIcon, AlertTriangle, Loader2, Check,
  Folder, MessageSquareQuote, Paperclip, GitMerge,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { EntityIcon } from "./AtlasTopicShared";


// ─────────────────────────────────────────────────────────────
// Normalization + similarity helpers.
// ─────────────────────────────────────────────────────────────
export function normalizeName(s) {
  return (s || "")
    .toLowerCase()
    .replace(/['"`.,!?()\[\]{}]/g, " ")
    .replace(/[-_/]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokenSetSim(a, b) {
  const A = new Set(normalizeName(a).split(" ").filter(Boolean));
  const B = new Set(normalizeName(b).split(" ").filter(Boolean));
  if (A.size === 0 || B.size === 0) return 0;
  let inter = 0;
  A.forEach((t) => { if (B.has(t)) inter += 1; });
  const union = A.size + B.size - inter;
  return union === 0 ? 0 : inter / union;
}

// Build clusters of children where every pairwise similarity >= threshold.
// Uses simple transitive union: if A~B and B~C we treat {A,B,C} as one
// cluster even if A~C is slightly below threshold, provided at least
// one bridge pair meets it. This matches the intent of "near-duplicate
// names" (typo/variant chains).
export function findDuplicateClusters(children, threshold = 0.75) {
  const arr = (children || []).filter((c) => c && c.slug);
  if (arr.length < 2) return [];
  const parent = arr.map((_, i) => i);
  const find = (x) => (parent[x] === x ? x : (parent[x] = find(parent[x])));
  const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };

  for (let i = 0; i < arr.length; i += 1) {
    for (let j = i + 1; j < arr.length; j += 1) {
      const sim = tokenSetSim(arr[i].name, arr[j].name);
      if (sim >= threshold) union(i, j);
    }
  }
  const buckets = new Map();
  for (let i = 0; i < arr.length; i += 1) {
    const r = find(i);
    if (!buckets.has(r)) buckets.set(r, []);
    buckets.get(r).push(arr[i]);
  }
  const clusters = [];
  buckets.forEach((rows) => {
    if (rows.length >= 2) clusters.push(rows);
  });
  return clusters;
}


// ─────────────────────────────────────────────────────────────
// Modal.
// ─────────────────────────────────────────────────────────────
export function MergeDuplicatesModal({ open, parent, children, onClose, onMerged }) {
  const clusters = useMemo(
    () => (open ? findDuplicateClusters(children || [], 0.75) : []),
    [open, children],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-start md:items-center justify-center overflow-y-auto p-3 md:p-4"
         data-testid="merge-duplicates-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-tb-navy/15 my-4 md:my-6 flex flex-col max-h-[92vh]">
        <div className="px-5 pt-5 pb-3 border-b border-tb-navy/10 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <GitMerge className="w-5 h-5 text-tb-navy" />
              <h2 className="text-tb-navy text-xl font-semibold"
                  style={{ fontFamily: '"Playfair Display", serif' }}>
                Merge near-duplicates under "{parent?.name || "topic"}"
              </h2>
            </div>
            <p className="mt-1 font-hand text-tb-navy/60 text-[11px]">
              Pick one topic per cluster to keep. The others' children, attributes,
              facts, and attachments will move into it and the duplicates will be deleted.
            </p>
          </div>
          <button type="button" onClick={onClose}
            data-testid="merge-close"
            className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-tb-navy/5 text-tb-navy/60">
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {clusters.length === 0 && (
            <div data-testid="merge-empty"
              className="rounded-2xl border border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-6 text-center font-hand text-tb-navy/60 text-sm">
              No near-duplicate clusters detected.
            </div>
          )}
          {clusters.map((cluster, idx) => (
            <ClusterCard key={cluster.map((c) => c.slug).join("|")}
              cluster={cluster}
              index={idx}
              onMerged={onMerged}
            />
          ))}
        </div>

        <div className="border-t border-tb-navy/10 px-5 py-4 flex items-center justify-end">
          <button type="button" onClick={onClose}
            data-testid="merge-done"
            className="h-9 px-5 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}


function ClusterCard({ cluster, index, onMerged }) {
  const [canonical, setCanonical] = useState(() => cluster[0]?.slug || null);
  const [enabled, setEnabled] = useState(() => new Set(cluster.map((c) => c.slug)));
  const [details, setDetails] = useState({});   // slug → { child_count, narrative_fact_count, attachment_count }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  // Fetch data footprint per row.
  useEffect(() => {
    let cancelled = false;
    Promise.all(cluster.map((c) => api.get(`/atlas/topics/${c.slug}`).catch(() => null)))
      .then((rs) => {
        if (cancelled) return;
        const next = {};
        rs.forEach((r, i) => {
          if (!r) return;
          const t = r.data?.topic || {};
          next[cluster[i].slug] = {
            child_count: r.data?.child_count || 0,
            fact_count: r.data?.narrative_fact_count || 0,
            attachment_count: (t.attachments || []).length,
            aliases: (t.aliases || []).length,
          };
        });
        setDetails(next);
        // Auto-pick the row with the most data as canonical.
        let best = cluster[0]?.slug;
        let bestScore = -1;
        cluster.forEach((c) => {
          const d = next[c.slug] || {};
          const score = (d.child_count || 0) * 3 + (d.fact_count || 0) * 2
                      + (d.attachment_count || 0) * 2 + (d.aliases || 0);
          if (score > bestScore) { bestScore = score; best = c.slug; }
        });
        setCanonical(best);
      });
    return () => { cancelled = true; };
  }, [cluster]);

  const toggleEnabled = (slug) => setEnabled((prev) => {
    const n = new Set(prev);
    if (n.has(slug)) n.delete(slug); else n.add(slug);
    return n;
  });

  const doMerge = async () => {
    if (!canonical) return;
    const dups = cluster.map((c) => c.slug).filter((s) => s !== canonical && enabled.has(s));
    if (dups.length === 0) {
      setErr("Pick at least one duplicate to merge in.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      const r = await api.post("/atlas/topics/merge", {
        canonical_slug: canonical,
        duplicate_slugs: dups,
      });
      const mergedCount = (r.data?.merged || []).length;
      const skippedCount = (r.data?.skipped || []).length;
      const canonicalName = cluster.find((c) => c.slug === canonical)?.name || canonical;
      if (skippedCount > 0) {
        toast.warning(`Merged ${mergedCount} · ${skippedCount} skipped`);
      } else {
        toast.success(`Merged ${mergedCount} topic${mergedCount === 1 ? "" : "s"} into "${canonicalName}"`);
      }
      setDone(true);
      onMerged?.();
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setErr(formatApiErrorDetail(detail) || "Merge failed.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`merge-cluster-done-${index}`}
        className="rounded-2xl border border-green-300 bg-green-50/60 p-4 font-hand text-green-800 text-sm flex items-center gap-2">
        <Check className="w-4 h-4" /> Cluster merged.
      </div>
    );
  }

  return (
    <div data-testid={`merge-cluster-${index}`}
      className="rounded-2xl border border-tb-navy/15 bg-white p-4">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/50">
          Cluster {index + 1}
        </span>
        <span className="font-hand text-[11px] text-tb-navy/70">
          {cluster.length} topics — pick one to keep, others will merge into it
        </span>
      </div>

      <div className="space-y-2">
        {cluster.map((row) => (
          <ClusterRow key={row.slug} row={row}
            details={details[row.slug]}
            isCanonical={canonical === row.slug}
            isEnabled={enabled.has(row.slug)}
            onSelectCanonical={() => setCanonical(row.slug)}
            onToggleEnabled={() => toggleEnabled(row.slug)}
          />
        ))}
      </div>

      {err && (
        <div data-testid={`merge-cluster-${index}-error`}
          className="mt-3 rounded-lg border border-red-300 bg-red-50 p-2.5 font-hand text-red-800 text-xs">
          {err}
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <button type="button" onClick={() => { setDone(true); }}
          data-testid={`merge-cluster-${index}-skip`}
          disabled={busy}
          className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
          Skip this cluster
        </button>
        <button type="button" onClick={doMerge} disabled={busy || !canonical}
          data-testid={`merge-cluster-${index}-confirm`}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <GitMerge className="w-3.5 h-3.5" />}
          Merge cluster
        </button>
      </div>
    </div>
  );
}


function ClusterRow({ row, details, isCanonical, isEnabled, onSelectCanonical, onToggleEnabled }) {
  const d = details || {};
  const rowCls = isCanonical
    ? "border-tb-navy bg-tb-navy/5"
    : "border-tb-navy/15 hover:bg-tb-navy/[0.02]";
  return (
    <div className={`rounded-xl border p-3 transition ${rowCls}`}
         data-testid={`merge-row-${row.slug}`}>
      <div className="flex items-start gap-3">
        <label className="flex items-center gap-2 cursor-pointer shrink-0 mt-0.5">
          <input type="radio" name={`canon-${row.slug}`}
            checked={isCanonical}
            onChange={onSelectCanonical}
            data-testid={`merge-canonical-radio-${row.slug}`}
            className="accent-tb-navy" />
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/50">
            Keep
          </span>
        </label>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <EntityIcon categoryId={row.category_id} className="w-3.5 h-3.5 text-tb-navy/60" />
            <h4 className="text-tb-navy text-sm font-semibold truncate"
                style={{ fontFamily: '"Playfair Display", serif' }}>
              {row.name}
            </h4>
            {isCanonical && (
              <span className="text-[9px] uppercase tracking-widest font-hand rounded-full bg-tb-navy text-tb-gold-bright px-2 py-0.5">
                Canonical
              </span>
            )}
          </div>
          <div className="mt-1 font-hand text-[11px] text-tb-navy/60">
            <code className="text-tb-navy/50">{row.slug}</code>
          </div>
          <div className="mt-1.5 flex items-center gap-3 flex-wrap font-hand text-[11px] text-tb-navy/70">
            <span className="inline-flex items-center gap-1">
              <Folder className="w-3 h-3" /> {d.child_count ?? "…"} children
            </span>
            <span className="inline-flex items-center gap-1">
              <MessageSquareQuote className="w-3 h-3" /> {d.fact_count ?? "…"} facts
            </span>
            <span className="inline-flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> {d.attachment_count ?? "…"} attachments
            </span>
          </div>
        </div>
        {!isCanonical && (
          <label className="flex items-center gap-2 shrink-0 cursor-pointer">
            <input type="checkbox" checked={isEnabled}
              onChange={onToggleEnabled}
              data-testid={`merge-include-checkbox-${row.slug}`}
              className="accent-tb-navy" />
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/50">
              Merge in
            </span>
          </label>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Amber pill CTA (F.6.2).
// ─────────────────────────────────────────────────────────────
export function DuplicateAlertPill({ children, parent, onReview }) {
  const clusters = useMemo(
    () => findDuplicateClusters(children || [], 0.75),
    [children],
  );
  const totalDups = clusters.reduce((n, c) => n + c.length, 0);
  if (clusters.length === 0 || totalDups < 2) return null;
  return (
    <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50/70 p-3 flex items-center justify-between gap-3 flex-wrap"
         data-testid="duplicate-alert-pill">
      <div className="flex items-center gap-2 min-w-0">
        <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
        <span className="font-hand text-sm text-amber-900">
          <b>{totalDups}</b> near-duplicate name{totalDups === 1 ? "" : "s"} detected
          under <b>{parent?.name || "this topic"}</b>
          {clusters.length > 1 && <> ({clusters.length} clusters)</>} — merge?
        </span>
      </div>
      <button type="button" onClick={onReview}
        data-testid="duplicate-alert-review"
        className="h-8 px-4 inline-flex items-center gap-1.5 rounded-full bg-amber-600 text-white font-hand text-xs hover:bg-amber-700 shrink-0">
        <GitMerge className="w-3.5 h-3.5" /> Review
      </button>
    </div>
  );
}
